/** Pure paging state for the server-cursor-driven review queue (INP-778).
 *
 *  The flashcard queue walks `GET /api/runs/{id}/findings?order=review`
 *  in offset pages. Two invariants shape this module:
 *
 *  1. **Locked membership** — once an id is loaded it keeps its slot for
 *     the life of the filter (the INP-328/373 UX: dispositioning a card
 *     must not make it vanish mid-review). `total` is likewise locked to
 *     the first page's server total so the "N of M" position indicator
 *     doesn't shrink while the practitioner works.
 *
 *  2. **Disposition-shift correction** — under a status filter (e.g.
 *     `status=pending`), every disposition removes a loaded row from the
 *     server's filtered set, so rows after the loaded window shift up by
 *     one. Fetching the next page at `offset = loadedCount` would skip
 *     exactly that many rows. The correction: the next fetch offset is
 *     the count of loaded ids whose status still matches what was loaded
 *     (`loadedCount - changedSinceLoad`). This is exact under a status
 *     filter (a status change always removes the row from the filter) and
 *     conservative without one (a changed row re-sorts later in the
 *     review order, so the lowered offset can only re-serve rows we
 *     already have — de-duped on append — never skip unseen ones; the
 *     rare unreview that re-sorts a row *earlier* concerns a card that is
 *     already loaded by construction).
 *
 *  A stall guard (`floorOffset`) forces forward progress if a fetch ever
 *  returns only already-loaded rows while the server still reports more.
 */

/** Page size for queue fetches. 100 rows ≈ the flashcard prefetch window. */
export const QUEUE_PAGE_SIZE = 100;

/** Start fetching the next page when the cursor is within this many
 *  cards of the loaded end, so the practitioner never hits the wall. */
export const QUEUE_PREFETCH_AHEAD = 25;

export interface PagerState {
  /** Loaded finding ids in locked server (review) order. */
  ids: string[];
  /** Status each id had when its page was fetched. */
  statusAtLoad: ReadonlyMap<string, string>;
  /** Server total at first load — locked for the position indicator. */
  total: number | null;
  /** True once the server reported no further page. */
  exhausted: boolean;
  /** Stall guard: minimum offset for the next fetch (see module doc). */
  floorOffset: number;
}

export function emptyPagerState(): PagerState {
  return { ids: [], statusAtLoad: new Map(), total: null, exhausted: false, floorOffset: 0 };
}

export interface PageResult {
  items: ReadonlyArray<{ finding_id: string; status: string }>;
  total: number | null | undefined;
  nextOffset: number | null | undefined;
  /** The offset this page was fetched at (for the stall guard). */
  offset: number;
}

/** Merge a fetched page: append unseen ids (locked order — existing ids
 *  keep their slots), record load-time statuses, lock `total` on first
 *  contact, and advance the stall guard when a page yielded nothing new. */
export function appendPage(state: PagerState, page: PageResult): PagerState {
  const seen = new Set(state.ids);
  const ids = [...state.ids];
  const statusAtLoad = new Map(state.statusAtLoad);
  let added = 0;
  for (const item of page.items) {
    if (seen.has(item.finding_id)) continue;
    seen.add(item.finding_id);
    ids.push(item.finding_id);
    statusAtLoad.set(item.finding_id, item.status);
    added += 1;
  }
  return {
    ids,
    statusAtLoad,
    total: state.total ?? page.total ?? null,
    exhausted: page.nextOffset === null || page.nextOffset === undefined,
    floorOffset:
      added === 0 && page.items.length > 0
        ? Math.max(state.floorOffset, page.offset + page.items.length)
        : state.floorOffset,
  };
}

/** How many loaded rows have changed status since their page was
 *  fetched — the disposition count that shifts later offsets. */
export function changedSinceLoad(
  state: PagerState,
  currentStatusOf: (id: string) => string | undefined,
): number {
  let changed = 0;
  for (const id of state.ids) {
    const loaded = state.statusAtLoad.get(id);
    const current = currentStatusOf(id);
    if (loaded !== undefined && current !== undefined && current !== loaded) changed += 1;
  }
  return changed;
}

/** Offset for the next page fetch (see module doc for the semantics). */
export function nextFetchOffset(state: PagerState, changed: number): number {
  return Math.max(0, state.ids.length - changed, state.floorOffset);
}

/** Should the queue fetch another page for the cursor to sit at `index`
 *  with a comfortable runway? False once the server is exhausted or the
 *  locked total is fully loaded. */
export function shouldFetchMore(state: PagerState, cursorIndex: number): boolean {
  if (state.exhausted) return false;
  if (state.total !== null && state.ids.length >= state.total) return false;
  return cursorIndex + QUEUE_PREFETCH_AHEAD >= state.ids.length;
}

/** One step of the next-pending walk: scan the loaded statuses from
 *  `from` for the first that `isPending` accepts. `found` lands the
 *  cursor there; `need-more` means the loaded window ran out while the
 *  server still has unloaded rows (fetch a page and rescan when it
 *  lands); `none` means the whole queue is loaded and nothing pending
 *  remains — stay put. */
export type PendingScan = { kind: 'found'; index: number } | { kind: 'need-more' } | { kind: 'none' };

export function scanForPending(
  statuses: ReadonlyArray<string>,
  from: number,
  total: number | null,
  isPending: (status: string) => boolean,
): PendingScan {
  for (let j = Math.max(0, from); j < statuses.length; j++) {
    if (isPending(statuses[j])) return { kind: 'found', index: j };
  }
  if (total !== null && statuses.length < total) return { kind: 'need-more' };
  return { kind: 'none' };
}

// ── Server-pushdown eligibility ──────────────────────────────────────

/** The slice of the review filter state that decides whether the queue
 *  can be served by server pages. Structural (not imported from the
 *  hook) so this module stays dependency-free and unit-testable. */
export interface ServerQueueFilterInputs {
  statusFilter: string;
  outcomeFilter: string;
  sourceFilter: string;
  ledgerSourceFilter: string;
  invoiceFilter: string;
  ruleFilter: string;
  provinceFilter: string;
  supplierFilter: string;
  transactionTypeFilter: string;
  classFilter: string;
  glAccountFilter: string;
  currencyFilter: string;
  approvalReasonFilter: string;
  reasonCodesCount: number;
  amountMin: string;
  amountMax: string;
  effRateMin: string;
  effRateMax: string;
  carriedOnlyFilter: boolean;
  search: string;
  /** True when the List view has a column sort applied. A sort spans the
   *  whole set, so it must use the full-set client path — this forces the
   *  pushdown to bail (null) exactly like a client-only filter. */
  listSortActive: boolean;
}

/** Query params for a server-driven queue, or `null` when the active
 *  filter combination can't be pushed down (a client-only filter is set,
 *  or the view includes auto-cleared rows, whose visibility toggle lives
 *  client-side). In the null case the queue falls back to the classic
 *  client-filtered path over the fully-loaded run.
 *
 *  Pushdown-able: status, outcome, expense source, ledger source, and
 *  the pure-attachment invoice filter (`attached` maps exactly to the
 *  server's invoice-link EXISTS; `unattached`/`no_invoice`/`complete`/
 *  `incomplete` depend on client-side triage + extraction state).
 *
 *  `outcome` always narrows to recovery + compliance_risk so the
 *  server pages exclude the auto-cleared `no_issue` coverage wall —
 *  mirroring `excludeAutoCleared` in the default client view. */
export function deriveServerQueueQuery(
  inputs: ServerQueueFilterInputs,
  includesAutoCleared: boolean,
): {
  status?: 'pending' | 'approved' | 'rejected' | 'needs_review';
  attached?: boolean;
  expense_source?: ('dext' | 'brex' | 'ramp' | 'direct')[];
  ledger_source?: ('xero' | 'qbo' | 'netsuite')[];
  outcome: ('recovery' | 'compliance_risk')[];
  order: 'review';
} | null {
  if (includesAutoCleared) return null;
  // A List column sort reorders the whole set client-side, so the paged
  // path (locked server review order) can't serve it — fall back.
  if (inputs.listSortActive) return null;
  const clientOnlyActive =
    inputs.ruleFilter !== '' ||
    inputs.provinceFilter !== '' ||
    inputs.supplierFilter !== '' ||
    inputs.transactionTypeFilter !== '' ||
    inputs.classFilter !== '' ||
    inputs.glAccountFilter.trim() !== '' ||
    inputs.currencyFilter !== '' ||
    inputs.approvalReasonFilter !== '' ||
    inputs.reasonCodesCount > 0 ||
    inputs.amountMin !== '' ||
    inputs.amountMax !== '' ||
    inputs.effRateMin !== '' ||
    inputs.effRateMax !== '' ||
    inputs.carriedOnlyFilter ||
    inputs.search.trim() !== '' ||
    (inputs.invoiceFilter !== '' && inputs.invoiceFilter !== 'attached');
  if (clientOnlyActive) return null;
  return {
    ...(inputs.statusFilter !== ''
      ? { status: inputs.statusFilter as 'pending' | 'approved' | 'rejected' | 'needs_review' }
      : {}),
    ...(inputs.invoiceFilter === 'attached' ? { attached: true } : {}),
    ...(inputs.sourceFilter !== ''
      ? { expense_source: [inputs.sourceFilter as 'dext' | 'brex' | 'ramp' | 'direct'] }
      : {}),
    ...(inputs.ledgerSourceFilter !== ''
      ? { ledger_source: [inputs.ledgerSourceFilter as 'xero' | 'qbo' | 'netsuite'] }
      : {}),
    outcome:
      inputs.outcomeFilter === 'recovery' || inputs.outcomeFilter === 'compliance_risk'
        ? [inputs.outcomeFilter]
        : ['recovery', 'compliance_risk'],
    order: 'review',
  };
}
